// AS LEITURAS DA NYTA: gráficos, relatórios e consultas montados a partir do banco (24/09/2026).
//
// A edge `nyta-chat` busca os dados (com a sessão de quem conversa) e chama estas funções, que
// são puras e testadas aqui. O modelo recebe o resultado pronto e só comenta: número nenhum
// nasce dele. A edge empacota este arquivo (`npm run build:nyta`).
//
// ⚠️ Nada de escape unicode no código (nem em string nem em regex): o deploy pela MCP decodifica
// esse escape uma vez a mais. O BOM do CSV sai por `String.fromCharCode`.

import type { GraficoDaConversa, MarcoDaLinha, SerieDaLinha } from './graficoDaConversa';

type Qualquer = Record<string, any>;

// ─── CSV ────────────────────────────────────────────────────────────────────

// Montado em tempo de execução: o minify dobraria `String.fromCharCode(0xfeff)` num escape unicode.
const BOM = [0xfeff].map((c) => String.fromCharCode(c)).join('');

const celula = (v: unknown): string => {
  if (v === null || v === undefined) return '';
  const s = typeof v === 'number' ? String(v).replace('.', ',') : String(v);
  return /[;"\n\r]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
};

/** CSV que o Excel brasileiro abre certo: separador `;`, vírgula decimal e BOM UTF-8. */
export function csv(cabecalho: string[], linhas: unknown[][]): string {
  return BOM + [cabecalho, ...linhas].map((l) => l.map(celula).join(';')).join('\r\n') + '\r\n';
}

// ─── Chartmetric (o JSON cru de `artist_chartmetric_raw`) ────────────────────

/** `where-people-listen`: { cities: { Nome: [{ timestp, listeners }] } }. */
export function cidadesDoWpl(wpl: Qualquer | null | undefined): Array<{ nome: string; pontos: Array<{ x: string; y: number }> }> {
  const cidades = wpl?.cities && typeof wpl.cities === 'object' ? wpl.cities : {};
  return Object.entries(cidades as Record<string, any[]>)
    .map(([nome, lista]) => ({
      nome,
      pontos: (Array.isArray(lista) ? lista : [])
        .filter((p) => p && typeof p.timestp === 'string' && Number.isFinite(Number(p.listeners)))
        .map((p) => ({ x: String(p.timestp).slice(0, 10), y: Number(p.listeners) }))
        .sort((a, b) => a.x.localeCompare(b.x)),
    }))
    .filter((c) => c.pontos.length > 0);
}

const ultimo = (pontos: Array<{ y: number }>) => pontos[pontos.length - 1]?.y ?? 0;

/** As cidades hoje: do `where-people-listen` (último ponto), ou do `cm_statistics` do artista. */
export function cidadesHoje(wpl: Qualquer | null | undefined, stats: Qualquer | null | undefined): Array<{ nome: string; ouvintes: number }> {
  const doWpl = cidadesDoWpl(wpl).map((c) => ({ nome: c.nome, ouvintes: ultimo(c.pontos) }));
  const lista = doWpl.length ? doWpl : (Array.isArray(stats?.sp_where_people_listen) ? stats!.sp_where_people_listen : [])
    .map((c: Qualquer) => ({ nome: String(c.name || ''), ouvintes: Number(c.listeners) || 0 }));
  return lista.filter((c: { nome: string }) => c.nome).sort((a: { ouvintes: number }, b: { ouvintes: number }) => b.ouvintes - a.ouvintes);
}

export const METRICAS = {
  ouvintes: { campo: 'sp_monthly_listeners', rotulo: 'Ouvintes mensais no Spotify' },
  seguidores: { campo: 'sp_followers', rotulo: 'Seguidores no Spotify' },
  popularidade: { campo: 'sp_popularity', rotulo: 'Popularidade no Spotify (0 a 100)' },
  playlists: { campo: 'num_sp_playlists', rotulo: 'Playlists no Spotify' },
  youtube: { campo: 'youtube_monthly_video_views', rotulo: 'Views mensais no YouTube' },
  tiktok: { campo: 'tiktok_followers', rotulo: 'Seguidores no TikTok' },
  instagram: { campo: 'ins_followers', rotulo: 'Seguidores no Instagram' },
} as const;
export type MetricaDosParecidos = keyof typeof METRICAS;

const num = (v: unknown): number | null => {
  const n = Number(v);
  return v === null || v === undefined || v === '' || !Number.isFinite(n) ? null : n;
};

export interface Parecido { nome: string; generos: string; stats: Qualquer; proprio?: boolean }

/** Os parecidos (`neighboring-artists`) e o próprio artista (`/api/artist/:id` → cm_statistics). */
export function parecidos(vizinhos: unknown, nomeProprio: string, statsProprios: Qualquer | null | undefined): Parecido[] {
  const lista = (Array.isArray(vizinhos) ? vizinhos : []).filter((v: Qualquer) => v && v.name)
    .map((v: Qualquer) => ({ nome: String(v.name), generos: String(v.genres || '').split(',').slice(0, 3).map((g) => g.trim()).filter(Boolean).join(', '), stats: v }));
  return statsProprios ? [{ nome: nomeProprio, generos: '', stats: statsProprios, proprio: true }, ...lista] : lista;
}

// ─── Gráficos ───────────────────────────────────────────────────────────────

export function graficoDasCidadesNoTempo(wpl: Qualquer | null | undefined, quantas = 5): GraficoDaConversa {
  const top = cidadesDoWpl(wpl).sort((a, b) => ultimo(b.pontos) - ultimo(a.pontos)).slice(0, quantas);
  return { tipo: 'linha', titulo: 'Ouvintes por cidade', unidade: 'ouvintes', fonte: 'Spotify, via Chartmetric', series: top.map((c) => ({ nome: c.nome, pontos: c.pontos })) };
}

export function graficoDasCidadesHoje(wpl: Qualquer | null | undefined, stats: Qualquer | null | undefined, quantas = 8): GraficoDaConversa {
  const top = cidadesHoje(wpl, stats).slice(0, quantas);
  return { tipo: 'barras', titulo: 'Onde te ouvem hoje', unidade: 'ouvintes', fonte: 'Spotify, via Chartmetric', categorias: top.map((c) => c.nome), series: [{ nome: 'Ouvintes', valores: top.map((c) => c.ouvintes) }] };
}

export function graficoDosParecidos(lista: Parecido[], metrica: MetricaDosParecidos = 'ouvintes', quantos = 10): GraficoDaConversa {
  const { campo, rotulo } = METRICAS[metrica] ?? METRICAS.ouvintes;
  const comValor = lista.map((p) => ({ ...p, valor: num(p.stats?.[campo]) })).filter((p) => p.valor !== null) as Array<Parecido & { valor: number }>;
  const ordenada = comValor.sort((a, b) => b.valor - a.valor);
  // O próprio artista entra sempre, mesmo fora do corte, para ele se ver na régua.
  let corte = ordenada.slice(0, quantos);
  const proprio = ordenada.find((p) => p.proprio);
  if (proprio && !corte.includes(proprio)) corte = [...corte.slice(0, quantos - 1), proprio];
  return {
    tipo: 'barras', titulo: `${rotulo}: você e artistas parecidos`, unidade: rotulo, fonte: 'Chartmetric (artistas parecidos)',
    categorias: corte.map((p) => p.nome), series: [{ nome: rotulo, valores: corte.map((p) => p.valor) }],
    destaque: corte.findIndex((p) => p.proprio),
  };
}

const DIMENSOES: Array<[string, string]> = [['r', 'Alcance'], ['e', 'Ganhos'], ['a', 'Público real'], ['l', 'Legitimação']];

/** As notas do REAL de agora, e as do anterior quando houver (0 a 100, acende em 70). */
export function graficoDoReal(atual: Qualquer | null | undefined, anterior?: Qualquer | null): GraficoDaConversa {
  const notas = (ri: Qualquer | null | undefined) => DIMENSOES.map(([d]) => Math.round(Number(ri?.boletim?.[d]) || 0));
  const series = [
    ...(anterior?.boletim ? [{ nome: `Anterior${anterior.computedAt ? ` (${String(anterior.computedAt).slice(0, 10)})` : ''}`, valores: notas(anterior) }] : []),
    { nome: `Atual${atual?.computedAt ? ` (${String(atual.computedAt).slice(0, 10)})` : ''}`, valores: notas(atual) },
  ];
  return { tipo: 'barras', titulo: 'Diagnóstico REAL: nota por frente (acende em 70)', unidade: 'nota', fonte: 'Diagnóstico REAL', categorias: DIMENSOES.map(([, n]) => n), series, maximo: 100 };
}

/** O que o artista fez, para cruzar com a curva: lançamentos, shows e ações concluídas. */
export function marcosDoArtista(dados: {
  projetos?: Array<{ title?: string; release_date?: string | null }>;
  eventos?: Array<{ title?: string; date?: string; type?: string }>;
  estrategias?: Array<{ actions?: Array<{ title?: string; status?: string; date?: string }> }>;
}, de: string, ate: string): MarcoDaLinha[] {
  const dentro = (x?: string | null) => !!x && x.slice(0, 10) >= de && x.slice(0, 10) <= ate;
  const marcos: MarcoDaLinha[] = [
    ...(dados.projetos || []).filter((p) => dentro(p.release_date)).map((p) => ({ x: String(p.release_date).slice(0, 10), rotulo: `Lançamento: ${p.title || 'música'}`, tipo: 'lancamento' as const })),
    ...(dados.eventos || []).filter((e) => e.type === 'show' && dentro(e.date)).map((e) => ({ x: String(e.date).slice(0, 10), rotulo: `Show: ${e.title || ''}`.trim(), tipo: 'show' as const })),
    ...(dados.estrategias || []).flatMap((s) => s.actions || []).filter((a) => a.status === 'done' && dentro(a.date)).map((a) => ({ x: String(a.date).slice(0, 10), rotulo: `Ação concluída: ${a.title || ''}`.trim(), tipo: 'acao' as const })),
  ];
  return marcos.sort((a, b) => a.x.localeCompare(b.x)).slice(0, 12);
}

/**
 * "Por que eu cresci?": a curva somada das cidades (o que o Spotify informa por cidade) com o que
 * o artista fez no mesmo período. Não é o total de ouvintes, e o título diz isso.
 */
export function graficoDaLinhaDoTempo(wpl: Qualquer | null | undefined, marcosDe: (de: string, ate: string) => MarcoDaLinha[]): GraficoDaConversa {
  const soma = new Map<string, number>();
  for (const c of cidadesDoWpl(wpl)) for (const p of c.pontos) soma.set(p.x, (soma.get(p.x) || 0) + p.y);
  const pontos = Array.from(soma.entries()).map(([x, y]) => ({ x, y })).sort((a, b) => a.x.localeCompare(b.x));
  const series: SerieDaLinha[] = pontos.length ? [{ nome: 'Ouvintes nas cidades informadas', pontos }] : [];
  const marcos = pontos.length ? marcosDe(pontos[0].x, pontos[pontos.length - 1].x) : [];
  return { tipo: 'linha', titulo: 'Linha do tempo: ouvintes e o que você fez', unidade: 'ouvintes', fonte: 'Spotify via Chartmetric, e o seu plano, músicas e agenda', series, marcos };
}

/** O que a Nyta lê do gráfico para comentar: os números que ele mostra, em texto curto. */
export function graficoEmTexto(g: GraficoDaConversa): string {
  if (g.tipo === 'barras') {
    return `${g.titulo}. ${g.categorias.map((c, i) => `${c}${g.destaque === i ? ' (o artista)' : ''}: ${g.series.map((s) => `${g.series.length > 1 ? `${s.nome} ` : ''}${s.valores[i] ?? '—'}`).join(', ')}`).join('; ')}.`;
  }
  const partes = g.series.map((s) => {
    const p = s.pontos;
    if (!p.length) return `${s.nome}: sem dados`;
    const a = p[0];
    const b = p[p.length - 1];
    const pico = p.reduce((m, x) => (x.y > m.y ? x : m), a);
    return `${s.nome}: ${a.y} em ${a.x}, ${b.y} em ${b.x}, pico de ${pico.y} em ${pico.x}`;
  });
  const marcos = (g.marcos || []).map((m) => `${m.x} ${m.rotulo}`).join('; ');
  return `${g.titulo}. ${partes.join('. ')}.${marcos ? ` Marcos no período: ${marcos}.` : ''}`;
}

// ─── Relatórios (CSV) ───────────────────────────────────────────────────────

const STATUS_MUSICA: Record<string, string> = { composition: 'Composição', production: 'Produção', mixing: 'Mixagem', mastering: 'Masterização', ready: 'Pronta', released: 'Lançada' };
const STATUS_TAREFA: Record<string, string> = { todo: 'A fazer', in_progress: 'Em andamento', done: 'Concluída', archived: 'Arquivada' };
const dataBr = (x?: string | null) => (x ? `${x.slice(8, 10)}/${x.slice(5, 7)}/${x.slice(0, 4)}` : '');

const TIPO_DE_EVENTO: Record<string, string> = { show: 'Show', reuniao: 'Reunião', estudio: 'Estúdio', ensaio: 'Ensaio', entrevista: 'Entrevista', outro: 'Outro', task: 'Ação do plano', release: 'Lançamento' };
const STATUS_DE_EVENTO: Record<string, string> = { scheduled: 'Agendado', confirmed: 'Confirmado', cancelled: 'Cancelado', completed: 'Concluído' };

export const relatorioDaAgenda = (eventos: Array<Qualquer>) => csv(
  ['Data', 'Início', 'Fim', 'Título', 'Tipo', 'Local', 'Status'],
  eventos.map((e) => [dataBr(e.date), (e.start_time || '').slice(0, 5), (e.end_time || '').slice(0, 5), e.title, TIPO_DE_EVENTO[e.type] || e.type || '', e.location || '', STATUS_DE_EVENTO[e.status] || e.status || '']),
);

export const relatorioDasMusicas = (projetos: Array<Qualquer>) => csv(
  ['Título', 'Status', 'Gênero', 'Lançamento', 'UPC'],
  projetos.map((p) => [p.title, STATUS_MUSICA[p.status] || p.status || '', p.genre || '', dataBr(p.release_date), p.upc || '']),
);

/** O plano como o artista vê: estratégia, ação, prazo, status e cada tarefa do checklist. */
export const relatorioDoPlano = (estrategias: Array<Qualquer>) => csv(
  ['Estratégia', 'Ação', 'Prazo', 'Status da ação', 'Tarefa', 'Status da tarefa'],
  estrategias.filter((s) => !s.arquivada).flatMap((s) => {
    const acoes = (s.actions || []).filter((a: Qualquer) => a.status !== 'archived');
    if (!acoes.length) return (s.tasks || []).filter((t: Qualquer) => t.status !== 'archived').map((t: Qualquer) => [s.title, '', dataBr(t.deadline), '', t.description, STATUS_TAREFA[t.status] || t.status]);
    return acoes.flatMap((a: Qualquer) => ((a.tasks || []).length ? a.tasks : [{}]).map((t: Qualquer) => [
      s.title, a.title, dataBr(a.date), STATUS_TAREFA[a.status] || a.status || '', t.description || '', t.status ? STATUS_TAREFA[t.status] || t.status : '',
    ]));
  }),
);

export const relatorioDasCidades = (wpl: Qualquer | null | undefined, stats: Qualquer | null | undefined) => {
  const hist = cidadesDoWpl(wpl);
  if (hist.length) {
    return csv(['Cidade', 'Data', 'Ouvintes'], hist.flatMap((c) => c.pontos.map((p) => [c.nome, dataBr(p.x), p.y])));
  }
  return csv(['Cidade', 'Ouvintes'], cidadesHoje(null, stats).map((c) => [c.nome, c.ouvintes]));
};

export const relatorioDosParecidos = (lista: Parecido[]) => csv(
  ['Artista', 'Gêneros', ...Object.values(METRICAS).map((m) => m.rotulo)],
  lista.map((p) => [p.proprio ? `${p.nome} (você)` : p.nome, p.generos, ...Object.values(METRICAS).map((m) => num(p.stats?.[m.campo]))]),
);

export const relatorioDasPlaylists = (playlists: unknown) => csv(
  ['Playlist', 'Curador', 'Seguidores', 'Música', 'Entrou em', 'Editorial'],
  (Array.isArray(playlists) ? playlists : []).map((x: Qualquer) => {
    const p = x?.playlist || {};
    return [p.name, p.curator_name || p.owner_name || '', num(p.followers), x?.track?.name || p.track || '', dataBr(p.added_at), p.editorial ? 'Sim' : 'Não'];
  }),
);

export const TIPOS_DE_RELATORIO = ['agenda', 'musicas', 'plano', 'cidades', 'parecidos', 'playlists'] as const;
export type TipoDeRelatorio = typeof TIPOS_DE_RELATORIO[number];

export const NOME_DO_RELATORIO: Record<TipoDeRelatorio, string> = {
  agenda: 'Agenda', musicas: 'Músicas', plano: 'Plano de ação', cidades: 'Cidades', parecidos: 'Artistas parecidos', playlists: 'Playlists',
};

// ─── Consulta em texto (para a Nyta responder) ───────────────────────────────

export function parecidosEmTexto(lista: Parecido[]): string {
  if (!lista.some((p) => !p.proprio)) return 'Ainda não há artistas parecidos nos dados do Chartmetric deste perfil.';
  const linha = (p: Parecido) => {
    const v = (c: string) => num(p.stats?.[c]);
    const partes = [
      v('sp_monthly_listeners') !== null && `${v('sp_monthly_listeners')} ouvintes mensais`,
      v('sp_followers') !== null && `${v('sp_followers')} seguidores no Spotify`,
      v('num_sp_playlists') !== null && `${v('num_sp_playlists')} playlists`,
      v('sp_popularity') !== null && `popularidade ${v('sp_popularity')}`,
    ].filter(Boolean);
    return `${p.proprio ? `${p.nome} (o artista)` : p.nome}${p.generos ? ` [${p.generos}]` : ''}: ${partes.join(', ') || 'sem números'}`;
  };
  return lista.map(linha).join('\n');
}
